import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AxiosError } from 'axios';
import { CONFLICT } from 'http-status-codes';

import emailButtonClick from '@hh.ru/analytics-js-events/build/xhh/common/vacancy_search/saved_search/vacancy_saved_search_email_button_click';
import emailSubscribeButtonClick from '@hh.ru/analytics-js-events/build/xhh/common/vacancy_search/saved_search/vacancy_saved_search_email_subscribe_button_click';
import messengersButtonClick from '@hh.ru/analytics-js-events/build/xhh/common/vacancy_search/saved_search/vacancy_saved_search_messengers_button_click';
import switchMessengersButtonClick from '@hh.ru/analytics-js-events/build/xhh/common/vacancy_search/saved_search/vacancy_saved_search_switch_messengers_button_click';
import {
    Banner,
    Button as MagritteButton,
    Text,
    Title,
    useBreakpoint,
    VSpacingContainer,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import urlParser from 'bloko/common/urlParser';

import AgreementHint from 'src/components/AdvertisementAgreement/AgreementHint';
import { useNotification } from 'src/components/Notifications/Provider';
import AutosearchModal from 'src/components/SupernovaSearch/VacancySavedSearchButton/AutosearchModal';
import Notification from 'src/components/SupernovaSearch/VacancySavedSearchButton/Notification';
import {
    FormState,
    FormStateType,
    SaveSearchError,
    SaveSearchErrorType,
} from 'src/components/SupernovaSearch/VacancySavedSearchButton/types';
import SavingClusters from 'src/components/VacancySearchContent/components/SavedSearchSubscription/SavingClusters';
import translation from 'src/components/translation';
import useOnOffState from 'src/hooks/useOnOffState';
import { useSelector } from 'src/hooks/useSelector';
import { saveAutoSearch } from 'src/models/search/vacancy/result';
import { UserType } from 'src/models/userType';
import { MapCriteria } from 'src/types/search/vacancy/criteria';
import fetcher from 'src/utils/fetcher';

const ANALYTICS_PLACE = 'vacancy_search_content';

const URL_SAVE = '/vacancysavedsearch/save';
const URL_POSTPONE = '/shards/vacancy_saved_search/postpone';

declare global {
    interface FetcherPostApi {
        [URL_SAVE]: {
            body: { email?: string; autosearchName?: string };
            queryParams: Partial<MapCriteria>;
            response: { state: string };
        };
        [URL_POSTPONE]: {
            body: { backUrl: string };
            queryParams: void;
            response: void;
        };
    }
}

const TrlKeys = {
    header: 'search.vacancy.savedSearch.header',
    buttonEmail: 'search.vacancy.savedSearch.form.button.email',
    buttonMessengers: 'search.vacancy.savedSearch.form.button.messengers',
    agreement: 'applicant.emailVacancySavedSearch.checkbox.text',
};

// TODO: унифицировать компоненты с src/components/SupernovaSearch/VacancySavedSearchButton/index.tsx
// https://jira.hh.ru/browse/HH-222161
const SavedSearchFromResults: TranslatedComponent = ({ trls }) => {
    const isAnonymous = useSelector(({ userType }) => userType === UserType.Anonymous);
    const { addNotification } = useNotification();
    const existChatBot = useSelector(({ chatBot }) => !!chatBot);
    const autosearch = useSelector(({ vacancySearchResult }) => vacancySearchResult.queries?.autosearch);
    const { pathname, search } = useSelector((state) => state.router.location);
    const { email, isSavedSearch, isFormOpen } = useSelector(
        ({ vacancySearchResult }) => vacancySearchResult.savedSearches
    );

    const dispatch = useDispatch();

    const { isMobile } = useBreakpoint();

    const [emailInputValue, setEmailInputValue] = useState('');
    const [emailError, setEmailError] = useState<SaveSearchErrorType | null>(null);

    const [formState, setFormState] = useState<FormStateType | null>(FormState.Saving);
    const [isLoading, setLoading, resetLoading] = useOnOffState(false);
    const [isModalEmailFormVisible, openModalEmailForm, hideModalEmailForm] = useOnOffState(false);
    const [isModalMessengersVisible, openModalMessengers, hideModalMessengers] = useOnOffState(false);

    const existEmail = !!email;
    const emailValue = email || emailInputValue;
    const saveWithoutForm = existEmail && !existChatBot;

    const hideModals = useCallback(() => {
        hideModalEmailForm();
        hideModalMessengers();
    }, [hideModalEmailForm, hideModalMessengers]);

    const saveSearch = useCallback(() => {
        setLoading();
        const parsedURL = urlParser(`?${autosearch || ''}`);
        const params = { ...parsedURL.params };
        fetcher
            .post(URL_SAVE, { email: emailValue }, { params })
            .then(() => {
                if (isMobile) {
                    setFormState(FormState.Saved);
                } else {
                    addNotification(Notification, {
                        props: { email: emailValue },
                    });
                }
                hideModals();
                dispatch(saveAutoSearch(emailValue));
                resetLoading();
            })
            .catch((error: AxiosError<{ state: string }>) => {
                if (error.response?.data?.state === SaveSearchError.WrongEmail) {
                    setEmailError(SaveSearchError.WrongEmail);
                } else if (error.response?.status === CONFLICT) {
                    setEmailError(SaveSearchError.DuplicateEmail);
                } else if (isMobile) {
                    setFormState(FormState.Error);
                } else {
                    addNotification(Notification, {
                        props: { email: emailValue, isError: true },
                    });
                }
                resetLoading();
            });
    }, [setLoading, autosearch, emailValue, isMobile, dispatch, resetLoading, addNotification, hideModals]);

    useEffect(() => {
        if (isFormOpen && saveWithoutForm) {
            saveSearch();
        }
    }, [isFormOpen, saveSearch, saveWithoutForm]);

    const onClickEmailSubscribe = useCallback(() => {
        if (existEmail) {
            emailButtonClick({
                existEmail: true,
                existChatBot,
                hhtmFromLabel: ANALYTICS_PLACE,
            });
        } else {
            emailSubscribeButtonClick({
                existChatBot,
                hhtmFromLabel: ANALYTICS_PLACE,
            });
        }
        saveSearch();
    }, [existChatBot, existEmail, saveSearch]);

    const onClickMessengers = useCallback(() => {
        if (existEmail) {
            messengersButtonClick({
                existEmail: true,
                hhtmFromLabel: ANALYTICS_PLACE,
            });
        } else {
            switchMessengersButtonClick({
                hhtmFromLabel: ANALYTICS_PLACE,
            });
        }
        openModalMessengers();
    }, [existEmail, openModalMessengers]);

    const onEmailButtonClick = useCallback(() => {
        if (isAnonymous) {
            const parsedLoginUrl = urlParser('/account/login');
            parsedLoginUrl.params.postponed = true;
            void fetcher.post(URL_POSTPONE, { backUrl: `${pathname}${search}` }).then(() => {
                window.location.assign(parsedLoginUrl.href);
            });
        } else if (existEmail) {
            onClickEmailSubscribe();
        } else {
            openModalEmailForm();
        }
    }, [existEmail, isAnonymous, onClickEmailSubscribe, openModalEmailForm, pathname, search]);

    const onEmailInputChange = (value: string) => {
        setEmailInputValue(value);
        if (emailError) {
            setEmailError(null);
        }
    };

    if (isSavedSearch) {
        return null;
    }

    return (
        <div data-qa="autosearch-subscribe__form">
            <Banner
                style="accent-secondary"
                title={
                    <Title Element="h5" size="small">
                        {trls[TrlKeys.header]}
                    </Title>
                }
                content={
                    <VSpacingContainer default={4}>
                        <Text>
                            <SavingClusters />
                        </Text>
                        <AgreementHint text={trls[TrlKeys.agreement]} articleId={31466} />
                    </VSpacingContainer>
                }
                buttonBase={
                    <MagritteButton
                        mode="primary"
                        style="accent"
                        size="small"
                        onClick={onEmailButtonClick}
                        data-qa="autosearch-subscribe__email-button"
                    >
                        {trls[TrlKeys.buttonEmail]}
                    </MagritteButton>
                }
                buttonAdditional={
                    existChatBot ? (
                        <MagritteButton
                            mode="primary"
                            style="accent"
                            size="small"
                            onClick={onClickMessengers}
                            data-qa="autosearch-subscribe__messengers-button"
                        >
                            {trls[TrlKeys.buttonMessengers]}
                        </MagritteButton>
                    ) : undefined
                }
                showClose={false}
                stretched
            />
            <AutosearchModal
                isDropVisible={isModalEmailFormVisible || isModalMessengersVisible}
                onDropClose={() => {
                    hideModals();
                    setFormState(FormState.Saving);
                }}
                isModalEmailFormVisible={isModalEmailFormVisible}
                isModalMessengersVisible={isModalMessengersVisible}
                hideModal={hideModals}
                existChatBot={existChatBot}
                userEmail={email}
                emailInputValue={emailInputValue}
                onEmailInputChange={onEmailInputChange}
                onClickEmailSubscribe={onClickEmailSubscribe}
                emailError={emailError}
                formState={formState}
                isLoading={isLoading}
                withoutMainContent
            />
            <VSpacing default={16} />
        </div>
    );
};

export default translation(SavedSearchFromResults);
